import { Random } from 'excalibur';
import { checkPlayerName, NameCheck } from './name-filter';
import { playerId } from './player';

export interface LeaderboardEntry {
  name: string;
  score: number;
  combo: number;
  hits: number;
  doubles: number;
}

export interface LeaderboardRow extends LeaderboardEntry {
  rank: number;
}

const NAME_KEY = 'crazy-clock-name';
const CUSTOM_NAME_KEY = 'crazy-clock-name-custom';

const ADJECTIVES = [
  'wobbly',
  'sleepy',
  'doodle',
  'sunny',
  'tickly',
  'cozy',
  'zippy',
  'merry',
  'minty',
  'inky',
];

const NOUNS = [
  'otter',
  'clock',
  'pencil',
  'cookie',
  'cloud',
  'sprout',
  'kettle',
  'pebble',
  'turnip',
  'crayon',
];

let endpoint = '';

export function configureLeaderboard(url: string): void {
  endpoint = url;
}

export function isLeaderboardEnabled(): boolean {
  return endpoint.length > 0;
}

export function playerName(): string {
  try {
    const stored = window.localStorage.getItem(NAME_KEY);
    if (stored) {
      return stored;
    }
  } catch {
    return 'doodler';
  }
  const rng = new Random(Date.now() % 2147483647);
  const name = `${rng.pickOne(ADJECTIVES)}-${rng.pickOne(NOUNS)}-${rng.integer(10, 99)}`;
  try {
    window.localStorage.setItem(NAME_KEY, name);
  } catch {
    return name;
  }
  return name;
}

export function hasCustomPlayerName(): boolean {
  try {
    return window.localStorage.getItem(CUSTOM_NAME_KEY) === '1';
  } catch {
    return false;
  }
}

export function savePlayerName(raw: string): NameCheck {
  const check = checkPlayerName(raw);
  if (!check.ok) {
    return check;
  }
  try {
    window.localStorage.setItem(NAME_KEY, check.name);
    window.localStorage.setItem(CUSTOM_NAME_KEY, '1');
  } catch {
    return check;
  }
  return check;
}

export async function renameScores(name: string): Promise<boolean> {
  if (!endpoint) {
    return false;
  }
  try {
    const response = await fetch(`${endpoint}/scores/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId: playerId(), name }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function submitScore(entry: LeaderboardEntry): Promise<number | null> {
  if (!endpoint) {
    return null;
  }
  try {
    const response = await fetch(`${endpoint}/scores`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...entry, playerId: playerId() }),
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as { rank?: number };
    return typeof data.rank === 'number' ? data.rank : null;
  } catch {
    return null;
  }
}

export async function fetchTopScores(limit = 10): Promise<LeaderboardRow[]> {
  if (!endpoint) {
    return [];
  }
  try {
    const response = await fetch(`${endpoint}/scores?limit=${limit}`);
    if (!response.ok) {
      return [];
    }
    const data = (await response.json()) as { scores?: LeaderboardRow[] };
    return Array.isArray(data.scores) ? data.scores : [];
  } catch {
    return [];
  }
}
